import React, { useEffect, useMemo, useState } from "react";
import styles from "./NamingRulesPage.module.css";
import { useLanguage } from "../../contexts/language/LanguageContext";
import {
  activateNamingRule,
  createNamingRule,
  deactivateNamingRule,
  deleteNamingRule,
  getNamingRules,
  updateNamingRule,
} from "../../services/naming";

const emptyForm = {
  code: "",
  pattern: "",
  target: "logical_document_name",
  document_type: "",
  is_active: true,
  description: "",
};

const defaultTokens = {
  record_code: "HS-2026-001",
  document_type: "hop_dong",
  physical_location_code: "KHO-A-KE12",
  signer: "Nguyen Van A",
  issue_date: "2026-04-14",
  document_no: "12/QD",
  doc_seq: 3,
};

const defaultBatchTokens = {
  ...defaultTokens,
  record_code: "HS-2026-014",
  physical_location_code: "KHO-B-KE3",
  doc_seq: 1,
};

const createSampleTokens = (target) => {
  if (target === "batch_name") {
    return [
      defaultBatchTokens,
      {
        ...defaultBatchTokens,
        document_type: "hoso_nhan_su",
        issue_date: "2026-01-09",
      },
      {
        ...defaultBatchTokens,
        document_type: "hop_dong",
        issue_date: "2026-02-17",
      },
    ];
  }

  return [
    defaultTokens,
    {
      ...defaultTokens,
      document_type: "vanban_hc",
      doc_seq: 8,
      issue_date: "2026-03-23",
      document_no: "86/TB",
    },
    {
      ...defaultTokens,
      document_type: "tailieu_mat",
      signer: "Tran Thi B",
      doc_seq: 14,
      issue_date: "2026-04-01",
      document_no: "12/QD",
    },
  ];
};

const formatToken = (value, format) => {
  if (value === null || value === undefined || value === "") return "unknown";
  if (!format) return String(value);

  if (/^0+$/.test(format)) {
    return String(value).padStart(format.length, "0");
  }

  if (format === "yyyyMMdd") {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}${mm}${dd}`;
  }

  return String(value);
};

const previewPattern = (pattern, tokenJson) => {
  if (!pattern) return "";
  return pattern.replace(
    /\{([a-zA-Z0-9_]+)(?::([^}]+))?\}/g,
    (_, token, format) => {
      const rawValue = tokenJson[token];
      return formatToken(rawValue, format);
    },
  );
};

const NamingRulesPage = () => {
  const { t } = useLanguage();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const targetOptions = useMemo(
    () => [
      {
        value: "logical_document_name",
        label: t("naming_target_document_title"),
      },
      { value: "batch_name", label: t("naming_target_batch_name") },
    ],
    [t],
  );

  const getTargetLabel = (target) => {
    return (
      targetOptions.find((option) => option.value === target)?.label || target
    );
  };

  const patternPreviewSamples = useMemo(() => {
    const samples = createSampleTokens(form.target).map((tokens) =>
      previewPattern(form.pattern, tokens),
    );
    const unique = samples
      .map((item) => String(item || "").trim())
      .filter(Boolean)
      .filter((item, index, arr) => arr.indexOf(item) === index);
    return unique;
  }, [form.pattern, form.target]);

  const loadRules = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getNamingRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || t("naming_rules_load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditingRule(null);
    setForm({ ...emptyForm });
    setEditorOpen(true);
    setError("");
  };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      code: rule.code || "",
      pattern: rule.pattern || "",
      target: rule.target || "logical_document_name",
      document_type: rule.document_type || "",
      is_active: rule.is_active !== false,
      description: rule.description || "",
    });
    setEditorOpen(true);
    setError("");
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditingRule(null);
    setForm({ ...emptyForm });
  };

  const onChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const sanitizeBody = (rawBody) => ({
    code: rawBody.code.trim(),
    pattern: rawBody.pattern.trim(),
    target: rawBody.target,
    document_type: rawBody.document_type.trim() || undefined,
    is_active: Boolean(rawBody.is_active),
    description: rawBody.description.trim() || undefined,
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.code.trim() || !form.pattern.trim() || !form.target) {
      setError(t("naming_rules_required_error"));
      return;
    }

    const body = sanitizeBody(form);

    setLoading(true);
    try {
      if (editingRule?._id) {
        await updateNamingRule(editingRule._id, body);
      } else {
        await createNamingRule(body);
      }
      closeEditor();
      await loadRules();
    } catch (e) {
      setError(e.message || t("naming_rules_save_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rule) => {
    const ok = window.confirm(
      t("naming_rules_confirm_delete", { code: rule.code || "" }),
    );
    if (!ok) return;

    setLoading(true);
    setError("");
    try {
      await deleteNamingRule(rule._id);
      await loadRules();
    } catch (e) {
      setError(e.message || t("naming_rules_delete_failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (rule) => {
    setLoading(true);
    setError("");
    try {
      if (rule.is_active) {
        await deactivateNamingRule(rule._id);
      } else {
        await activateNamingRule(rule._id);
      }
      await loadRules();
    } catch (e) {
      setError(e.message || t("naming_rules_toggle_failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>{t("naming_rules_title")}</h2>
            <p className={styles.subtitle}>{t("naming_rules_subtitle")}</p>
          </div>
          <div className={styles.actions}>
            <button
              className={styles.btn}
              onClick={loadRules}
              disabled={loading}
            >
              {t("naming_rules_reload")}
            </button>
            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={openCreate}
              disabled={loading}
            >
              {t("naming_rules_create")}
            </button>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.tableWrap}>
          <div className={styles.table}>
            <div className={`${styles.row} ${styles.head}`}>
              <div>{t("naming_rules_col_code")}</div>
              <div>{t("naming_rules_col_pattern")}</div>
              <div>{t("naming_rules_col_target")}</div>
              <div>{t("naming_rules_col_doc_type")}</div>
              <div>{t("naming_rules_col_status")}</div>
              <div>{t("naming_rules_col_actions")}</div>
            </div>

            {rules.map((rule) => (
              <div className={styles.row} key={rule._id || rule.code}>
                <div>{rule.code}</div>
                <div className={styles.pattern}>{rule.pattern}</div>
                <div>{getTargetLabel(rule.target)}</div>
                <div>
                  {rule.document_type ? (
                    <span className={`${styles.tag} ${styles.tagDocType}`}>
                      {rule.document_type}
                    </span>
                  ) : (
                    <span className={`${styles.tag} ${styles.tagDefault}`}>
                      {t("naming_rules_global_default")}
                    </span>
                  )}
                </div>
                <div>
                  <span
                    className={`${styles.tag} ${rule.is_active ? styles.statusOn : styles.statusOff}`}
                  >
                    {rule.is_active
                      ? t("naming_rules_status_active")
                      : t("naming_rules_status_inactive")}
                  </span>
                </div>
                <div className={styles.rowActions}>
                  <button
                    className={styles.linkBtn}
                    onClick={() => openEdit(rule)}
                  >
                    {t("naming_rules_edit")}
                  </button>
                  <button
                    className={styles.linkBtn}
                    onClick={() => handleToggleActive(rule)}
                  >
                    {rule.is_active
                      ? t("naming_rules_deactivate")
                      : t("naming_rules_activate")}
                  </button>
                  <button
                    className={`${styles.linkBtn} ${styles.linkDanger}`}
                    onClick={() => handleDelete(rule)}
                  >
                    {t("naming_rules_delete")}
                  </button>
                </div>
              </div>
            ))}

            {!rules.length && !loading && (
              <div className={styles.empty}>{t("naming_rules_empty")}</div>
            )}
          </div>
        </div>
      </div>

      {editorOpen && (
        <div className={styles.backdrop}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>
              {editingRule
                ? t("naming_rules_edit_title")
                : t("naming_rules_create_title")}
            </h3>

            <form onSubmit={handleSubmit}>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span className={styles.label}>
                    {t("naming_rules_field_code")}{" "}
                    <span className={styles.required}>*</span>
                  </span>
                  <input
                    className={styles.input}
                    value={form.code}
                    onChange={(e) => onChange("code", e.target.value)}
                    disabled={Boolean(editingRule)}
                    placeholder="rule_cong_van_den"
                  />
                </label>

                <label className={styles.field}>
                  <span className={styles.label}>
                    {t("naming_rules_field_target")}{" "}
                    <span className={styles.required}>*</span>
                  </span>
                  <select
                    className={styles.select}
                    value={form.target}
                    onChange={(e) => onChange("target", e.target.value)}
                  >
                    {targetOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className={`${styles.field} ${styles.fieldFull}`}>
                  <span className={styles.label}>
                    {t("naming_rules_field_pattern")}{" "}
                    <span className={styles.required}>*</span>
                  </span>
                  <textarea
                    className={styles.textarea}
                    value={form.pattern}
                    onChange={(e) => onChange("pattern", e.target.value)}
                    placeholder="{record_code}_{document_type}_{doc_seq:003}"
                  />
                </label>

                <label className={styles.field}>
                  <span className={styles.label}>
                    {t("naming_rules_field_document_type")}
                  </span>
                  <input
                    className={styles.input}
                    value={form.document_type}
                    onChange={(e) => onChange("document_type", e.target.value)}
                    placeholder={t(
                      "naming_rules_field_document_type_placeholder",
                    )}
                  />
                  <span className={styles.helper}>
                    {t("naming_rules_field_document_type_hint")}
                  </span>
                </label>

                <label className={styles.field}>
                  <span className={styles.label}>
                    {t("naming_rules_field_description")}
                  </span>
                  <input
                    className={styles.input}
                    value={form.description}
                    onChange={(e) => onChange("description", e.target.value)}
                    placeholder={t(
                      "naming_rules_field_description_placeholder",
                    )}
                  />
                </label>

                <label className={styles.field}>
                  <span className={styles.label}>
                    {t("naming_rules_field_active")}
                  </span>
                  <select
                    className={styles.select}
                    value={form.is_active ? "true" : "false"}
                    onChange={(e) =>
                      onChange("is_active", e.target.value === "true")
                    }
                  >
                    <option value="true">
                      {t("naming_rules_status_active")}
                    </option>
                    <option value="false">
                      {t("naming_rules_status_inactive")}
                    </option>
                  </select>
                </label>

                <div className={`${styles.field} ${styles.fieldFull}`}>
                  <span className={styles.label}>
                    {t("naming_rules_test_preview")}
                  </span>
                  <div className={styles.sampleBox}>
                    {patternPreviewSamples.length ? (
                      <div className={styles.previewList}>
                        {patternPreviewSamples.map((sample, index) => (
                          <div
                            key={`${sample}-${index}`}
                            className={styles.previewText}
                          >
                            {sample}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.previewText}>-</div>
                    )}
                  </div>
                  <span className={styles.helper}>
                    {t("naming_rules_auto_preview_hint")}
                  </span>
                </div>
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.btn}
                  onClick={closeEditor}
                >
                  {t("naming_rules_cancel")}
                </button>
                <button
                  type="submit"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                >
                  {t("naming_rules_save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NamingRulesPage;
